<script setup>
/**
 * Pipeline Onboarding Tour Component
 * UX/UI Sprint - Task B3: Onboarding Tour
 *
 * Visual component for the guided tour overlay and tooltips.
 * Uses Teleport to render at document root for proper z-index handling.
 */

import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useId,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  getTopmostDashboardModalLayer,
  registerDashboardModalLayer,
  unregisterDashboardModalLayer,
} from 'dashboard/components/dashboardModalStack';

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false,
  },
  currentStep: {
    type: Object,
    default: null,
  },
  currentStepIndex: {
    type: Number,
    default: 0,
  },
  totalSteps: {
    type: Number,
    default: 0,
  },
  progress: {
    type: Number,
    default: 0,
  },
  isFirstStep: {
    type: Boolean,
    default: true,
  },
  isLastStep: {
    type: Boolean,
    default: false,
  },
  targetRect: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['next', 'prev', 'skip', 'complete']);

const { t } = useI18n();
const spotlightMaskId = `pipeline-tour-spotlight-${useId()}`;
const dialogRef = ref(null);
const tooltipViewportRef = ref(null);
const tooltipContentRef = ref(null);
const isTopmostInStack = ref(false);
const modalStackEntry = {
  containerRef: dialogRef,
  isTopmost: isTopmostInStack,
  maskRef: dialogRef,
};
let previouslyFocusedElement = null;
let hasBodyScrollLock = false;
let hasModalStackRegistration = false;
let tooltipResizeObserver = null;

// True when target element is absent on a step that expected one
const isTargetMissing = computed(
  () =>
    !!props.currentStep?.target &&
    props.currentStep.position !== 'center' &&
    !props.targetRect
);

const hasVisibleHighlight = computed(
  () =>
    props.currentStep?.highlight &&
    !isTargetMissing.value &&
    Boolean(props.targetRect)
);
const stepIndicator = computed(
  () => `${props.currentStepIndex + 1} / ${props.totalSteps}`
);
const keyboardKeyLabels = {
  previous: '←',
  next: '→',
  separator: '|',
  escape: 'Esc',
};

const VIEWPORT_PADDING = 16;
const TARGET_GAP = 16;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_ESTIMATED_HEIGHT = 360;
const viewport = ref({
  width: typeof window === 'undefined' ? 0 : window.innerWidth,
  height: typeof window === 'undefined' ? 0 : window.innerHeight,
});
const tooltipSize = ref({
  width: TOOLTIP_MAX_WIDTH,
  height: TOOLTIP_ESTIMATED_HEIGHT,
});

const updateViewport = () => {
  viewport.value = {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizedTargetRect = computed(() => {
  if (!props.targetRect) return null;

  const left = Number(props.targetRect.left) || 0;
  const top = Number(props.targetRect.top) || 0;
  const width = Number(props.targetRect.width) || 0;
  const height = Number(props.targetRect.height) || 0;

  return {
    left,
    top,
    width,
    height,
    right: Number(props.targetRect.right) || left + width,
    bottom: Number(props.targetRect.bottom) || top + height,
  };
});

const tooltipPlacement = computed(() => {
  const viewportWidth = Math.max(
    viewport.value.width,
    VIEWPORT_PADDING * 2 + 1
  );
  const viewportHeight = Math.max(
    viewport.value.height,
    VIEWPORT_PADDING * 2 + 1
  );
  const width = Math.min(
    tooltipSize.value.width,
    viewportWidth - VIEWPORT_PADDING * 2
  );
  const height = Math.min(
    tooltipSize.value.height,
    viewportHeight - VIEWPORT_PADDING * 2
  );
  const rect = normalizedTargetRect.value;

  if (
    !rect ||
    isTargetMissing.value ||
    props.currentStep?.position === 'center'
  ) {
    return {
      placement: 'center',
      x: Math.round((viewportWidth - width) / 2),
      y: Math.round((viewportHeight - height) / 2),
      width,
      height,
    };
  }

  const availableSpace = {
    top: rect.top - VIEWPORT_PADDING - TARGET_GAP,
    bottom: viewportHeight - rect.bottom - VIEWPORT_PADDING - TARGET_GAP,
    left: rect.left - VIEWPORT_PADDING - TARGET_GAP,
    right: viewportWidth - rect.right - VIEWPORT_PADDING - TARGET_GAP,
  };
  const requiredSpace = {
    top: height,
    bottom: height,
    left: width,
    right: width,
  };
  const oppositePlacement = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };
  const preferredPlacement = ['top', 'bottom', 'left', 'right'].includes(
    props.currentStep?.position
  )
    ? props.currentStep.position
    : 'bottom';
  const candidates = [
    preferredPlacement,
    oppositePlacement[preferredPlacement],
    ...['bottom', 'top', 'right', 'left'].filter(
      placement =>
        placement !== preferredPlacement &&
        placement !== oppositePlacement[preferredPlacement]
    ),
  ];
  const fittingPlacement = candidates.find(
    placement => availableSpace[placement] >= requiredSpace[placement]
  );
  const placement =
    fittingPlacement ||
    candidates.reduce((best, candidate) =>
      availableSpace[candidate] > availableSpace[best] ? candidate : best
    );

  let x;
  let y;

  if (placement === 'top' || placement === 'bottom') {
    x = rect.left + rect.width / 2 - width / 2;
    y =
      placement === 'top'
        ? rect.top - TARGET_GAP - height
        : rect.bottom + TARGET_GAP;
  } else {
    x =
      placement === 'left'
        ? rect.left - TARGET_GAP - width
        : rect.right + TARGET_GAP;
    y = rect.top + rect.height / 2 - height / 2;
  }

  return {
    placement,
    x: Math.round(
      clamp(x, VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING)
    ),
    y: Math.round(
      clamp(y, VIEWPORT_PADDING, viewportHeight - height - VIEWPORT_PADDING)
    ),
    width,
    height,
  };
});

const updateTooltipSize = ({ width, height }) => {
  if (![width, height].every(value => Number.isFinite(value) && value > 0)) {
    return;
  }

  const nextSize = {
    width: Math.min(Math.round(width), TOOLTIP_MAX_WIDTH),
    height: Math.round(height),
  };
  if (
    nextSize.width === tooltipSize.value.width &&
    nextSize.height === tooltipSize.value.height
  ) {
    return;
  }
  tooltipSize.value = nextSize;
};

const measureTooltip = () => {
  if (!tooltipContentRef.value) return;

  const borderBox = tooltipContentRef.value.getBoundingClientRect();
  updateTooltipSize({
    width: Math.max(
      TOOLTIP_MAX_WIDTH,
      borderBox.width,
      tooltipContentRef.value.scrollWidth
    ),
    height: Math.max(borderBox.height, tooltipContentRef.value.scrollHeight),
  });
};

const stopTooltipObserver = () => {
  tooltipResizeObserver?.disconnect();
  tooltipResizeObserver = null;
};

const startTooltipObserver = () => {
  stopTooltipObserver();
  measureTooltip();
  if (!tooltipContentRef.value || typeof ResizeObserver === 'undefined') {
    return;
  }

  tooltipResizeObserver = new ResizeObserver(measureTooltip);
  tooltipResizeObserver.observe(tooltipContentRef.value);
};

const handleViewportResize = async () => {
  updateViewport();
  await nextTick();
  measureTooltip();
};

const highlightRect = computed(() => {
  const rect = normalizedTargetRect.value;
  if (!rect || !props.currentStep?.highlight) return null;
  const padding = 8;

  return {
    x: rect.left - padding,
    y: rect.top - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
});

const handleNext = () => {
  if (props.isLastStep) {
    emit('complete');
  } else {
    emit('next');
  }
};

const handlePrev = () => {
  emit('prev');
};

const handleSkip = () => {
  emit('skip');
};

const focusableSelector =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const lockBodyScroll = () => {
  if (hasBodyScrollLock || !document.body) return;

  const { body } = document;
  const lockCount = Number.parseInt(
    body.dataset.dashboardModalLockCount || '0',
    10
  );

  if (lockCount === 0) {
    body.dataset.dashboardModalPreviousOverflow = body.style.overflow;
  }

  body.dataset.dashboardModalLockCount = String(lockCount + 1);
  body.style.overflow = 'hidden';
  hasBodyScrollLock = true;
};

const unlockBodyScroll = () => {
  if (!hasBodyScrollLock || !document.body) return;

  const { body } = document;
  const lockCount = Math.max(
    0,
    Number.parseInt(body.dataset.dashboardModalLockCount || '1', 10) - 1
  );

  if (lockCount === 0) {
    body.style.overflow = body.dataset.dashboardModalPreviousOverflow || '';
    delete body.dataset.dashboardModalPreviousOverflow;
    delete body.dataset.dashboardModalLockCount;
  } else {
    body.dataset.dashboardModalLockCount = String(lockCount);
  }

  hasBodyScrollLock = false;
};

const registerTourLayer = () => {
  if (hasModalStackRegistration) return;

  registerDashboardModalLayer(modalStackEntry);
  hasModalStackRegistration = true;
};

const unregisterTourLayer = () => {
  if (!hasModalStackRegistration) return;

  unregisterDashboardModalLayer(modalStackEntry);
  hasModalStackRegistration = false;
};

const restorePreviousFocus = () => {
  const previousFocus = previouslyFocusedElement;
  const topmostLayer = getTopmostDashboardModalLayer();
  previouslyFocusedElement = null;

  if (topmostLayer) {
    const topmostContainer = topmostLayer.containerRef.value;
    const target =
      (previousFocus?.isConnected && topmostContainer?.contains(previousFocus)
        ? previousFocus
        : topmostContainer?.querySelector(focusableSelector)) ||
      topmostContainer;
    target?.focus({ preventScroll: true });
    return;
  }

  if (previousFocus?.isConnected) {
    previousFocus.focus({ preventScroll: true });
  }
};

const containDialogFocus = () => {
  if (
    !props.isActive ||
    !isTopmostInStack.value ||
    !dialogRef.value ||
    dialogRef.value.contains(document.activeElement)
  ) {
    return;
  }
  dialogRef.value.focus({ preventScroll: true });
};

const handleDocumentFocusIn = event => {
  if (
    props.isActive &&
    isTopmostInStack.value &&
    dialogRef.value &&
    !dialogRef.value.contains(event.target)
  ) {
    dialogRef.value.focus({ preventScroll: true });
  }
};

const handleDialogKeydown = event => {
  if (!isTopmostInStack.value) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    handleSkip();
    return;
  }

  if (event.key === 'ArrowRight') {
    event.preventDefault();
    event.stopPropagation();
    handleNext();
    return;
  }

  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    event.stopPropagation();
    if (!props.isFirstStep) handlePrev();
    return;
  }

  if (event.key !== 'Tab') return;

  const focusable = [
    ...(dialogRef.value?.querySelectorAll(focusableSelector) || []),
  ];
  if (!focusable.length) {
    event.preventDefault();
    dialogRef.value?.focus({ preventScroll: true });
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const activeElement = document.activeElement;
  const focusIsOutsideDialog = !dialogRef.value?.contains(activeElement);

  if (
    event.shiftKey &&
    (activeElement === first ||
      activeElement === dialogRef.value ||
      focusIsOutsideDialog)
  ) {
    event.preventDefault();
    last.focus();
  } else if (
    !event.shiftKey &&
    (activeElement === last ||
      activeElement === dialogRef.value ||
      focusIsOutsideDialog)
  ) {
    event.preventDefault();
    first.focus();
  }
};

watch(
  () => props.isActive,
  async isActive => {
    if (isActive) {
      previouslyFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      registerTourLayer();
      lockBodyScroll();
      document.addEventListener('focusin', handleDocumentFocusIn, true);
      await nextTick();
      if (props.isActive && isTopmostInStack.value) {
        dialogRef.value?.focus({ preventScroll: true });
        startTooltipObserver();
      }
      return;
    }

    const shouldRestoreFocus =
      isTopmostInStack.value ||
      dialogRef.value?.contains(document.activeElement);
    document.removeEventListener('focusin', handleDocumentFocusIn, true);
    stopTooltipObserver();
    unregisterTourLayer();
    unlockBodyScroll();
    await nextTick();
    if (shouldRestoreFocus) {
      restorePreviousFocus();
    } else {
      previouslyFocusedElement = null;
    }
  },
  { immediate: true }
);

watch(isTopmostInStack, async isTopmost => {
  if (!props.isActive || !isTopmost) return;

  await nextTick();
  if (props.isActive && isTopmostInStack.value) {
    dialogRef.value?.focus({ preventScroll: true });
  }
});

watch(
  [() => props.currentStepIndex, () => props.currentStep],
  async () => {
    if (!props.isActive) return;

    await nextTick();
    containDialogFocus();
    startTooltipObserver();
  },
  { deep: true }
);

onMounted(() => {
  window.addEventListener('resize', handleViewportResize);
});

onUnmounted(() => {
  const shouldRestoreFocus =
    isTopmostInStack.value || dialogRef.value?.contains(document.activeElement);
  window.removeEventListener('resize', handleViewportResize);
  document.removeEventListener('focusin', handleDocumentFocusIn, true);
  stopTooltipObserver();
  unregisterTourLayer();
  unlockBodyScroll();
  if (shouldRestoreFocus) {
    restorePreviousFocus();
  } else {
    previouslyFocusedElement = null;
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-150 motion-reduce:transition-none"
      leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isActive"
        ref="dialogRef"
        data-test="pipeline-tour-dialog"
        class="pointer-events-none fixed inset-0"
        :class="isTopmostInStack ? 'z-[10000]' : 'z-[9990]'"
        role="dialog"
        :aria-hidden="isTopmostInStack ? undefined : 'true'"
        :aria-modal="isTopmostInStack ? 'true' : undefined"
        aria-labelledby="pipeline-tour-title"
        aria-describedby="pipeline-tour-description"
        :inert="isTopmostInStack ? undefined : ''"
        tabindex="-1"
        @keydown="handleDialogKeydown"
      >
        <!-- Overlay backdrop -->
        <div
          v-if="!hasVisibleHighlight"
          data-test="pipeline-tour-backdrop"
          class="pointer-events-auto fixed inset-0 cursor-pointer bg-black/40 backdrop-blur-sm dark:bg-black/60"
          aria-hidden="true"
          @click="handleSkip"
        />

        <svg
          v-else
          data-test="pipeline-tour-spotlight"
          class="pointer-events-auto fixed inset-0 size-full cursor-pointer"
          aria-hidden="true"
          @click="handleSkip"
        >
          <defs>
            <mask
              :id="spotlightMaskId"
              maskUnits="userSpaceOnUse"
              maskContentUnits="userSpaceOnUse"
            >
              <rect width="100%" height="100%" class="fill-white" />
              <rect
                :x="highlightRect.x"
                :y="highlightRect.y"
                :width="highlightRect.width"
                :height="highlightRect.height"
                rx="8"
                class="fill-black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            :mask="`url(#${spotlightMaskId})`"
            class="fill-black opacity-60 dark:opacity-75"
          />
        </svg>

        <!-- Highlight box around target element -->
        <svg
          v-if="highlightRect"
          class="pointer-events-none fixed inset-0 z-[9310] size-full overflow-visible"
          aria-hidden="true"
        >
          <rect
            :x="highlightRect.x"
            :y="highlightRect.y"
            :width="highlightRect.width"
            :height="highlightRect.height"
            rx="8"
            class="fill-transparent stroke-n-blue-9 stroke-[4] motion-reduce:transition-none"
          />
        </svg>

        <!-- Tooltip card -->
        <svg
          class="pointer-events-none fixed inset-0 z-[9320] size-full overflow-visible"
        >
          <foreignObject
            :x="tooltipPlacement.x"
            :y="tooltipPlacement.y"
            :width="tooltipPlacement.width"
            :height="tooltipPlacement.height"
            overflow="visible"
            :data-placement="tooltipPlacement.placement"
          >
            <div
              ref="tooltipViewportRef"
              data-test="pipeline-tour-tooltip"
              class="pointer-events-auto max-h-full w-full overflow-y-auto rounded-xl bg-n-solid-2 shadow-xl"
            >
              <div
                ref="tooltipContentRef"
                data-test="pipeline-tour-tooltip-content"
                class="w-full"
              >
                <!-- Progress bar -->
                <ProgressBar
                  :value="progress"
                  size="sm"
                  class="!h-1 rounded-none"
                  :label="t('PIPELINE.TOUR.PROGRESS_LABEL')"
                />

                <!-- Content -->
                <div class="p-4 sm:p-5" aria-live="polite" aria-atomic="true">
                  <!-- Step indicator -->
                  <div class="mb-2 text-xs font-medium text-n-slate-10">
                    {{ stepIndicator }}
                  </div>

                  <!-- Title -->
                  <h3
                    id="pipeline-tour-title"
                    class="m-0 text-lg font-semibold leading-snug text-n-slate-12"
                  >
                    {{ currentStep?.title }}
                  </h3>

                  <!-- Description -->
                  <p
                    id="pipeline-tour-description"
                    class="m-0 mt-2 text-sm leading-5 text-n-slate-10"
                  >
                    {{ currentStep?.content }}
                  </p>
                </div>

                <!-- Actions -->
                <div
                  class="flex items-center justify-between gap-2 border-t border-n-weak bg-n-alpha-1 px-4 py-3 sm:px-5"
                >
                  <button
                    v-if="currentStep?.showSkip !== false && !isLastStep"
                    type="button"
                    class="min-h-11 rounded-lg px-3 text-sm text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                    @click="handleSkip"
                  >
                    {{ t('PIPELINE.TOUR.SKIP') }}
                  </button>

                  <div class="ml-auto flex gap-2">
                    <Button
                      v-if="!isFirstStep"
                      data-test="pipeline-tour-previous"
                      variant="faded"
                      color="slate"
                      size="md"
                      class="min-h-11"
                      @click="handlePrev"
                    >
                      {{ t('PIPELINE.TOUR.PREVIOUS') }}
                    </Button>

                    <Button
                      variant="solid"
                      color="blue"
                      size="md"
                      class="min-h-11"
                      @click="handleNext"
                    >
                      {{
                        isLastStep
                          ? t('PIPELINE.TOUR.FINISH')
                          : t('PIPELINE.TOUR.NEXT')
                      }}
                    </Button>
                  </div>
                </div>

                <!-- Keyboard hint -->
                <div
                  class="bg-n-alpha-1 px-4 pb-3 text-center text-[11px] text-n-slate-9 sm:px-5"
                >
                  <!-- Arrow / Esc glyphs are universal keyboard symbols -->
                  <kbd
                    class="mx-0.5 inline-block rounded border border-n-weak bg-n-alpha-2 px-1.5 py-0.5 font-sans text-[10px]"
                  >
                    {{ keyboardKeyLabels.previous }}
                  </kbd>
                  <kbd
                    class="mx-0.5 inline-block rounded border border-n-weak bg-n-alpha-2 px-1.5 py-0.5 font-sans text-[10px]"
                  >
                    {{ keyboardKeyLabels.next }}
                  </kbd>
                  {{ t('PIPELINE.TOUR.KEYBOARD_NAV') }}
                  <span class="mx-2">{{ keyboardKeyLabels.separator }}</span>
                  <kbd
                    class="mx-0.5 inline-block rounded border border-n-weak bg-n-alpha-2 px-1.5 py-0.5 font-sans text-[10px]"
                  >
                    {{ keyboardKeyLabels.escape }}
                  </kbd>
                  {{ t('PIPELINE.TOUR.TO_SKIP') }}
                </div>
              </div>
            </div>
          </foreignObject>
        </svg>
      </div>
    </Transition>
  </Teleport>
</template>
